import React, { useState, useEffect } from 'react';
import { PauseCircle, CheckCircle, RefreshCw, CookingPot, Clock } from 'lucide-react';
import { API_URL } from '../../api/config';

const KOTCard = ({ ticket, viewMode }) => {
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTimeSince = (dateString) => {
    const minutes = Math.floor((new Date() - new Date(dateString)) / 60000);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m ago`;
  };

  const statusColor = ticket.type === 'HOLD' ? '#F59E0B' : '#10B981';
  const statusText = ticket.type === 'HOLD' ? 'On Hold' : 'Completed';
  const StatusIcon = ticket.type === 'HOLD' ? PauseCircle : CheckCircle;

  return (
    <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden border border-gray-100">
      <div className="flex justify-between items-center p-3" style={{ backgroundColor: statusColor }}>
        <div className="flex items-center gap-2">
          <span className="text-white text-xl font-bold">Table {ticket.tableNumber}</span>
          <span className="bg-white bg-opacity-30 text-white text-xs px-2 py-1 rounded-full">
            {statusText}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <StatusIcon size={18} color="white" />
          <span className="text-white font-medium">{formatTime(ticket.createdAt)}</span>
        </div>
      </div>

      <div className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {ticket.items.map((item, index) => (
            <div 
              key={index} 
              className="flex justify-between items-center py-2 px-3 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors duration-200"
            >
              <div className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ 
                    backgroundColor: item.category === 'Veg' ? '#10B981' : '#EF4444' 
                  }}
                />
                <div>
                  <div className="text-base font-medium">{item.itemName}</div>
                  {item.variant && (
                    <div className={`text-sm text-gray-500 ${viewMode === 'grid' ? 'truncate' : ''}`}>
                      {item.variant.name}
                    </div>
                  )}
                </div>
              </div>
              <span className="text-base font-bold bg-gray-100 px-2 py-1 rounded-full">×{item.quantity}</span>
            </div>
          ))}
        </div>

        <div className="flex justify-between mt-4 pt-2 border-t border-gray-100">
          <div className="flex items-center gap-1 text-gray-500">
            <Clock size={14} />
            <span className="text-sm">{getTimeSince(ticket.createdAt)}</span>
          </div>
          {ticket.names && (
            <div className="flex items-center gap-1 text-gray-500">
              <span className="text-sm italic">Names: {ticket.names}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const KOTDisplay = () => {
  // Set default view mode to 'list'
  const [viewMode, setViewMode] = useState('list'); // 'grid' or 'list'
  const [kotData, setKotData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'hold', 'completed'

  const fetchKOTData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      // Fetch hold bills
      const holdBillsResponse = await fetch(`${API_URL}/hold-bills`, {
        method: 'GET',
        headers,
      });

      // Fetch regular bills
      const billsResponse = await fetch(`${API_URL}/bills`, {
        method: 'GET',
        headers,
      });

      if (!holdBillsResponse.ok || !billsResponse.ok) {
        throw new Error('Failed to fetch data');
      }

      const holdBills = await holdBillsResponse.json();
      const billsData = await billsResponse.json();

      // Filter completed bills from today
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const completedBills = billsData.bills.filter(bill => {
        const billDate = new Date(bill.createdAt);
        return bill.status === 'COMPLETED' && billDate >= today;
      });

      // Combine and format the data
      const formattedData = [
        ...holdBills.map((bill) => ({
          ...bill,
          type: 'HOLD',
        })),
        ...completedBills.map((bill) => ({
          ...bill,
          type: 'COMPLETED',
        })),
      ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      setKotData(formattedData);
      setError(null);
    } catch (error) {
      console.error('Error fetching KOT data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchKOTData();
    const interval = setInterval(fetchKOTData, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchKOTData();
  };

  const filteredData = kotData.filter(ticket => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'hold') return ticket.type === 'HOLD';
    if (filterStatus === 'completed') return ticket.type === 'COMPLETED';
    return true;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading kitchen tickets...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="sticky top-0 z-10 bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center py-4">
            <div className="flex items-center mb-4 sm:mb-0">
              <CookingPot className="text-blue-600 mr-2" size={24} />
              <h1 className="text-2xl font-bold text-gray-800">Kitchen Order Tickets</h1>
            </div>
            
            <div className="flex flex-wrap gap-2">
              <button 
                onClick={() => setFilterStatus('all')}
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  filterStatus === 'all' 
                    ? 'bg-gray-800 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                All Tickets
              </button>
              <button 
                onClick={() => setFilterStatus('hold')}
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  filterStatus === 'hold' 
                    ? 'bg-amber-500 text-white' 
                    : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                }`}
              >
                On Hold
              </button>
              <button 
                onClick={() => setFilterStatus('completed')}
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  filterStatus === 'completed' 
                    ? 'bg-emerald-500 text-white' 
                    : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                }`}
              >
                Completed
              </button>
            </div>
          </div>
          
          <div className="flex justify-between items-center pb-4 border-b border-gray-200">
          <div className="flex items-center gap-4">
  {/* Grid button on the left */}
  <button 
    onClick={() => setViewMode('grid')}
    className={`p-2 rounded ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'text-gray-500 hover:bg-gray-100'}`}
    title="Grid View"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-5 w-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
      />
    </svg>
  </button>

  {/* List button on the right */}
  <button
    onClick={() => setViewMode('list')}
    className={`p-2 rounded ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'text-gray-500 hover:bg-gray-100'}`}
    title="List View"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-5 w-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 6h16M4 12h16M4 18h16"
      />
    </svg>
  </button>
</div>

            
            <button 
              onClick={handleRefresh}
              disabled={refreshing}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors duration-200"
            >
              {refreshing ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  Refreshing...
                </>
              ) : (
                <>
                  <RefreshCw size={14} />
                  Refresh
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {error ? (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        ) : filteredData.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <h3 className="mt-2 text-lg font-medium text-gray-900">No orders found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {filterStatus !== 'all' ? 'Try changing your filter or' : 'There are currently no kitchen tickets to display.'} 
            </p>
            {filterStatus !== 'all' && (
              <button
                onClick={() => setFilterStatus('all')}
                className="mt-3 text-sm text-blue-600 hover:text-blue-800"
              >
                View all tickets
              </button>
            )}
          </div>
        ) : (
          <div className={`grid gap-6 ${
            viewMode === 'grid' 
              ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' 
              : 'grid-cols-1'
          }`}>
            {filteredData.map((ticket) => (
              <KOTCard key={ticket._id} ticket={ticket} viewMode={viewMode} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default KOTDisplay;
